import { HttpEventType } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { format } from 'date-fns';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { filter, finalize, map, merge, switchMap, take } from 'rxjs';
import { CommonService, FileUploadService, NgbModalService } from '@zayan-erp/shared';
import { SalesApsService } from '../../../../services/sales-aps.service';
import { ExternalDocument } from 'libs/sales/src/lib/models/deal-document.models';
import { DealDocumentsService } from '@zayan-erp/sales';

@Component({
  selector: 'lib-upload-external-contract',
  standalone: true,
  imports: [FormsModule, DatePickerModule],
  templateUrl: './upload-external-contract.html'
})
export class UploadExternalContract {
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #dealDocumentsService = inject(DealDocumentsService);
  readonly #fileUploadService = inject(FileUploadService);
  readonly #salesApsService = inject(SalesApsService);
  readonly #commonService = inject(CommonService);

  uploadExternalContractModal = viewChild<TemplateRef<unknown>>('uploadExternalContractModal');
  externalContractFileInputRef = viewChild<ElementRef<HTMLInputElement>>('externalContractFileInputRef');

  dealId = input.required<string>();
  document = input<ExternalDocument | null>(null);
  uploaded = output<void>();

  externalContractFile = signal<File | null>(null);
  externalContractApprovalDate = signal<Date | null>(null);
  externalContractSignedDate = signal<Date | null>(null);
  readonly today = new Date();
  externalContractDescription = signal<string | null>(null);
  externalContractUploading = signal(false);
  showExternalContractValidation = signal(false);

  constructor() {
    effect(() => {
      this.externalContractDescription.set(this.document()?.note ?? null);
    });

    this.#dealDocumentsService.openUploadExternalContractModal$
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => {
        this.openModal();
      });
  }

  openModal(): void {
    this.resetModal();
    const modalRef = this.#modalService.open(this.uploadExternalContractModal(), {
      ...this.#ngbModalService.centerModal,
      size: 'lg'
    });
    merge(modalRef.closed, modalRef.dismissed).subscribe(() => this.resetModal());
  }

  onExternalContractFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    this.externalContractFile.set(file);
    this.showExternalContractValidation.set(false);
  }

  removeExternalContractFile(): void {
    this.externalContractFile.set(null);
  }

  formatFileSize(bytes: number): string {
    return this.#commonService.formatFileSize(bytes);
  }

  openExternalContractFilePicker(): void {
    this.externalContractFileInputRef()?.nativeElement.click();
  }

  submitExternalContract(modal: { close: (reason: string) => void }): void {
    const dealId = this.dealId();
    const file = this.externalContractFile();
    const approvalDate = this.externalContractApprovalDate();
    const signedDate = this.externalContractSignedDate();

    // Date signed is required on upload; on replace it's optional (blank keeps the current one).
    if (!file || (!this.document() && (!approvalDate || !signedDate))) {
      this.showExternalContractValidation.set(true);
      return;
    }
    const apsSignDate = signedDate ? format(signedDate, 'yyyy-MM-dd') : null;

    this.externalContractUploading.set(true);
    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            filter((uploadEvent: any) => uploadEvent?.type === HttpEventType.Response),
            take(1),
            map(() => awsData)
          )
        ),
        switchMap(awsData =>
          this.document()
            ? this.#dealDocumentsService.replaceExternalSignedDocument(dealId, {
                externalDocumentId: awsData.documentId,
                note: this.externalContractDescription(),
                approvalDate: approvalDate ? format(approvalDate, 'yyyy-MM-dd') : null,
                apsSignDate
              })
            : this.#dealDocumentsService.externalSignedDocument(dealId, {
                externalDocumentId: awsData.documentId,
                note: this.externalContractDescription(),
                approvalDate: approvalDate ? format(approvalDate, 'yyyy-MM-dd') : null,
                apsSignDate
              })
        ),
        finalize(() => {
          this.externalContractUploading.set(false);
        })
      )
      .subscribe({
        next: () => {
          this.#salesApsService.realoadDeal.next(null);
          this.uploaded.emit();
          modal.close('uploaded');
        }
      });
  }

  private resetModal(): void {
    this.externalContractFile.set(null);
    this.externalContractApprovalDate.set(null);
    this.externalContractSignedDate.set(null);
    this.externalContractUploading.set(false);
    this.showExternalContractValidation.set(false);
  }
}
